import type { FlagContext } from './evaluate';

export const REQUEST_STATUSES = ['submitted', 'approved', 'under-review', 'planned', 'in-progress', 'delivered', 'not-planned'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  submitted: 'Submitted', approved: 'Approved', 'under-review': 'Under review', planned: 'Planned',
  'in-progress': 'In progress', delivered: 'Delivered', 'not-planned': 'Not planned',
};

export interface RequestScope {
  environment?: string;
  product: string;
  userId: string;
  authorName: string;
  context?: FlagContext;
}

export interface FeatureRequest {
  id: string;
  product: string;
  title: string;
  description: string;
  status: RequestStatus;
  voteCount: number;
  voted: boolean;
  enhancementCount: number;
  createdAt: string;
  updatedAt: string;
  version: number;
  hidden: boolean;
  approvedAt?: string | null;
}

export interface RequestEnhancement {
  id: string;
  description: string;
  authorName: string;
  createdAt: string;
}

export interface FeatureRequestDetail extends FeatureRequest {
  enhancements: RequestEnhancement[];
}

export interface RequestFilters {
  query?: string;
  order?: 'latest' | 'votes';
  status?: RequestStatus | '';
  offset?: number;
}

export interface RequestPage {
  requests: FeatureRequest[];
  hasMore: boolean;
  canSubmit?: boolean;
}

export interface SimilarRequest {
  request: FeatureRequest;
  reason: string;
}

export interface RequestComparison {
  matches: SimilarRequest[];
  checkedAt: string;
}

export interface CreateRequestInput {
  title: string;
  description: string;
  submissionId: string;
  confirmDistinct?: boolean;
}

export type CreateRequestResult =
  | { created: true; request: FeatureRequestDetail }
  | { created: false; comparison: RequestComparison };

export interface EnhancementInput {
  description: string;
  submissionId: string;
}

/** Browser callbacks call the consuming app's authenticated backend. */
export interface FeatureRequestsApi {
  list: (filters: RequestFilters) => Promise<RequestPage>;
  detail: (id: string) => Promise<FeatureRequestDetail>;
  compare: (description: string) => Promise<RequestComparison>;
  create: (input: CreateRequestInput) => Promise<CreateRequestResult>;
  vote: (id: string, voted: boolean) => Promise<FeatureRequest>;
  enhance: (id: string, input: EnhancementInput) => Promise<FeatureRequestDetail>;
}
